import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { plantUploadStoragePath, uploadImage } from '../services/storage';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { usePlants } from '../hooks/usePlants';
import { identifyPlant, generateCareInfo, fetchReferencePhoto, withRetry, isRateLimitError } from '../services/api';
import { PhotoCapture } from '../components/plants/PhotoCapture';
import { IdentificationResult } from '../components/plants/IdentificationResult';
import { PlantDetailsForm, PlantFormData } from '../components/plants/PlantDetailsForm';
import { NameInput } from '../components/plants/NameInput';
import { NameSuggestions } from '../components/plants/NameSuggestions';
import { SaveSuccessModal } from '../components/plants/SaveSuccessModal';
import { RateLimitMessage } from '../components/identification/RateLimitMessage';
import { MismatchRecovery } from '../components/identification/MismatchRecovery';
import type { RecoveryStep, AdditionalContext, IdentificationData, AlternativeIdentification } from '../types/api';
import type { AnyCareInfo } from '../types/plant';

type AddPlantStep = 'method' | 'capture' | 'identifying' | 'result' | 'details' | 'saving' | 'manual' | 'validating' | 'suggestions' | 'custom-details' | 'rate-limited' | 'recovery';

// Plant-themed loading messages for delight
const IDENTIFYING_MESSAGES = [
  'Examining leaves and stems...',
  'Consulting the plant database...',
  'Matching patterns...',
  'Almost there...',
];

const SAVING_MESSAGES = [
  'Preparing care instructions...',
  'Adding to your garden...',
  'Almost ready...',
];

export default function AddPlant() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const { addPlant, canAddPlant } = usePlants();

  const [step, setStep] = useState<AddPlantStep>('method');
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string>('');
  const [photoUrl, setPhotoUrl] = useState<string>('');
  // Recorded alongside the URL so deleting the plant can delete the file
  // without guessing its path. See Plant.photoStoragePath.
  const [photoStoragePath, setPhotoStoragePath] = useState<string>('');
  const [referencePhotoUrl, setReferencePhotoUrl] = useState<string>('');
  const [identification, setIdentification] = useState<IdentificationData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [manualName, setManualName] = useState<string>('');
  const [suggestions, setSuggestions] = useState<AlternativeIdentification[]>([]);
  const [isCustomPlant, setIsCustomPlant] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [savedPlantId, setSavedPlantId] = useState<string>('');
  const [rateLimitResetMs, setRateLimitResetMs] = useState<number>(0);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [recoveryView, setRecoveryView] = useState<RecoveryStep>('options');
  const [wrongIdentificationName, setWrongIdentificationName] = useState<string>('');
  const [isRecoveryProcessing, setIsRecoveryProcessing] = useState(false);

  // Rotate loading messages for delight
  const isLoading = step === 'identifying' || step === 'saving' || step === 'validating';
  useEffect(() => {
    if (!isLoading) {
      setLoadingMessageIndex(0);
      return;
    }
    const interval = setInterval(() => {
      setLoadingMessageIndex((prev) => prev + 1);
    }, 2000);
    return () => clearInterval(interval);
  }, [isLoading]);

  // Helper to fetch reference photo in background
  const fetchReference = (commonName: string, scientificName: string) => {
    setReferencePhotoUrl('');
    fetchReferencePhoto({ commonName, scientificName })
      .then((result) => setReferencePhotoUrl(result.photoUrl))
      .catch(() => {});
  };

  // Helper to upload photo and run identification
  const uploadAndIdentify = async (
    file: File,
    previewUrl: string,
    errorStep: AddPlantStep,
    onError?: () => void,
  ) => {
    if (!user) return;

    setPhotoPreviewUrl(previewUrl);
    setError(null);
    setStep('identifying');

    try {
      const uploadPath = plantUploadStoragePath(user.uid);
      const uploadedUrl = await uploadImage(uploadPath, file);
      setPhotoUrl(uploadedUrl);
      setPhotoStoragePath(uploadPath);

      const identifyResult = await withRetry(() => identifyPlant({ imageUrl: uploadedUrl }));
      const id = identifyResult.identification;
      setIdentification({
        commonName: id.commonName,
        scientificName: id.scientificName,
        confidence: id.confidence,
        alternatives: id.alternatives,
      });

      fetchReference(id.commonName, id.scientificName);
      setStep('result');
    } catch (err) {
      console.error('Identification failed:', err);
      if (isRateLimitError(err)) {
        setRateLimitResetMs(err.resetInMs);
        setStep('rate-limited');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to identify plant. Please try again.');
        setStep(errorStep);
        onError?.();
      }
    }
  };

  // Handle method selection
  const handlePhotoMethod = () => {
    if (!canAddPlant) {
      showError('You have reached the plant limit for free accounts.');
      return;
    }
    setStep('capture');
  };

  const handleManualMethod = () => {
    if (!canAddPlant) {
      showError('You have reached the plant limit for free accounts.');
      return;
    }
    setStep('manual');
  };

  // Handle manual name validation
  const handleValidateName = async () => {
    if (manualName.trim().length < 2) {
      setError('Please enter at least 2 characters');
      return;
    }

    setError(null);
    setStep('validating');

    try {
      // Call identify API with text-only input
      const result = await withRetry(() => identifyPlant({ plantName: manualName.trim() }));

      const id = result.identification;

      // If high confidence match, go directly to result
      if (id.confidence === 'high') {
        setIdentification({
          commonName: id.commonName,
          scientificName: id.scientificName,
          confidence: id.confidence,
          alternatives: id.alternatives,
        });

        fetchReference(id.commonName, id.scientificName);
        setStep('result');
      } else if (id.alternatives && id.alternatives.length > 0) {
        // Show suggestions for lower confidence
        setSuggestions([
          { commonName: id.commonName, scientificName: id.scientificName },
          ...id.alternatives,
        ]);
        setStep('suggestions');
      } else {
        // No match - offer custom plant option
        setSuggestions([]);
        setStep('suggestions');
      }
    } catch (err) {
      console.error('Name validation failed:', err);
      if (isRateLimitError(err)) {
        setRateLimitResetMs(err.resetInMs);
        setStep('rate-limited');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to validate plant name. Please try again.');
        setStep('manual');
      }
    }
  };

  // Handle selecting a suggestion
  const handleSelectSuggestion = async (suggestion: AlternativeIdentification) => {
    setIdentification({
      commonName: suggestion.commonName,
      scientificName: suggestion.scientificName,
      confidence: 'high', // User explicitly selected
      alternatives: undefined,
    });

    fetchReference(suggestion.commonName, suggestion.scientificName);
    setStep('details');
  };

  // Handle adding as custom plant
  const handleAddCustom = () => {
    setIsCustomPlant(true);
    setIdentification({
      commonName: manualName.trim(),
      scientificName: '',
      confidence: 'low',
      alternatives: undefined,
    });
    setStep('custom-details');
  };

  // Handle photo capture
  const handlePhotoCapture = (file: File, previewUrl: string) =>
    uploadAndIdentify(file, previewUrl, 'capture');

  // Handle confirmation of identification
  const handleConfirmIdentification = async () => {
    if (!identification) return;

    setStep('details');
  };

  // Handle selecting an alternative
  const handleSelectAlternative = async (alt: AlternativeIdentification) => {
    setIdentification({
      ...identification!,
      commonName: alt.commonName,
      scientificName: alt.scientificName,
      confidence: 'high', // User explicitly selected this
      alternatives: undefined,
    });

    fetchReference(alt.commonName, alt.scientificName);
  };

  // Handle saving the plant (photo-based or manual with care info)
  const handleSavePlant = async (formData: PlantFormData) => {
    if (!identification) return;

    setStep('saving');
    setError(null);

    try {
      // Generate care info (skip for custom plants)
      let plantCareInfo: AnyCareInfo | null = null;
      if (!isCustomPlant && identification.scientificName) {
        try {
          const careResult = await withRetry(() =>
            generateCareInfo({
              commonName: identification.commonName,
              scientificName: identification.scientificName,
            })
          );
          plantCareInfo = careResult.careInfo;
        } catch {
          // Care info is optional, continue without it
        }
      }

      // Save plant
      const result = await addPlant({
        commonName: identification.commonName,
        scientificName: identification.scientificName || null,
        photoURL: photoUrl || referencePhotoUrl || '/placeholder-plant.svg',
        // Only when the saved photo IS our upload — a path that doesn't match
        // the URL would point deletion at a file the plant never displayed.
        photoStoragePath: photoUrl ? photoStoragePath : null,
        careInfo: plantCareInfo,
        nickname: formData.nickname || null,
        location: formData.location || null,
        acquiredDate: formData.acquiredDate || null,
        isCustom: isCustomPlant,
      });

      if (result.success && result.id) {
        setSavedPlantId(result.id);
        setShowSuccessModal(true);
      } else {
        setError(result.error || 'Failed to save plant');
        setStep(isCustomPlant ? 'custom-details' : 'details');
      }
    } catch (err) {
      console.error('Save failed:', err);
      setError('Failed to save plant. Please try again.');
      setStep(isCustomPlant ? 'custom-details' : 'details');
    }
  };

  // Handle success modal actions
  const handleViewPlant = () => {
    setShowSuccessModal(false);
    if (identification) {
      showSuccess(`${identification.commonName} added to your collection!`);
    }
    navigate(`/plant/${savedPlantId}`);
  };

  const handleAddAnother = () => {
    setShowSuccessModal(false);
    if (identification) {
      showSuccess(`${identification.commonName} added to your collection!`);
    }
    // Reset state for adding another plant
    setStep('method');
    setPhotoPreviewUrl('');
    setPhotoUrl('');
    setPhotoStoragePath('');
    setReferencePhotoUrl('');
    setIdentification(null);
    setError(null);
    setManualName('');
    setSuggestions([]);
    setIsCustomPlant(false);
    setSavedPlantId('');
  };

  const handleDone = () => {
    setShowSuccessModal(false);
    if (identification) {
      showSuccess(`${identification.commonName} added to your collection!`);
    }
    navigate('/');
  };

  // Handle "Not my plant" — enter recovery flow
  const handleNotMyPlant = () => {
    if (identification) {
      setWrongIdentificationName(identification.commonName);
    }
    setRecoveryView('options');
    setError(null);
    setStep('recovery');
  };

  // Handle recovery description submission
  const handleRecoveryDescribe = async (context: AdditionalContext) => {
    if (!photoUrl) return;

    setIsRecoveryProcessing(true);
    setError(null);

    try {
      const result = await withRetry(() =>
        identifyPlant({
          imageUrl: photoUrl,
          additionalContext: {
            ...context,
            previousIdentification: wrongIdentificationName || undefined,
          },
        })
      );

      const id = result.identification;

      if (id.commonName === 'Unknown' || id.confidence === 'low') {
        setError('Still unable to identify. Try retaking the photo or entering the name manually.');
        return;
      }

      setIdentification({
        commonName: id.commonName,
        scientificName: id.scientificName,
        confidence: id.confidence,
        alternatives: id.alternatives,
      });

      fetchReference(id.commonName, id.scientificName);
      setStep('result');
    } catch (err) {
      console.error('Recovery identification failed:', err);
      if (isRateLimitError(err)) {
        setRateLimitResetMs(err.resetInMs);
        setStep('rate-limited');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to identify plant. Please try again.');
      }
    } finally {
      setIsRecoveryProcessing(false);
    }
  };

  // Handle recovery retake — new photo captured, do fresh identification
  const handleRecoveryRetake = (file: File, previewUrl: string) =>
    uploadAndIdentify(file, previewUrl, 'recovery', () => setRecoveryView('retake'));

  // Handle going back
  const handleBack = () => {
    switch (step) {
      case 'capture':
        setStep('method');
        break;
      case 'manual':
        setStep('method');
        setManualName('');
        setError(null);
        break;
      case 'suggestions':
        setStep('manual');
        setSuggestions([]);
        break;
      case 'result':
        // If we came from manual entry, go back there
        if (!photoUrl) {
          setStep('manual');
        } else {
          setStep('capture');
        }
        setIdentification(null);
        setReferencePhotoUrl('');
        break;
      case 'details':
        setStep('result');
        break;
      case 'custom-details':
        setStep('suggestions');
        setIsCustomPlant(false);
        setIdentification(null);
        break;
      case 'recovery':
        setStep('result');
        setRecoveryView('options');
        setError(null);
        break;
      case 'rate-limited':
        setStep('method');
        setRateLimitResetMs(0);
        break;
      default:
        navigate(-1);
    }
  };

  return (
    <main className="min-h-screen bg-background">
      {/* Error display */}
      {error && (
        <div className="mx-6 mb-4 rounded-lg bg-danger/5 border border-danger/20 p-4 text-sm font-sans text-danger" role="alert">
          {error}
        </div>
      )}

      {/* Step: Method selection — editorial layout */}
      {step === 'method' && (
        <div className="flex flex-col min-h-[calc(100vh-140px)]">
          <p className="text-center text-lg italic text-gray-600 font-serif mt-2 mb-8 px-6">
            How would you like to add your plant?
          </p>

          <div className="editorial-divider mx-6 mb-8" />

          <div className="flex-1 px-6 flex flex-col gap-6">
            {/* Take a Photo — dark card with background */}
            <button
              onClick={handlePhotoMethod}
              className="group w-full relative overflow-hidden rounded-lg bg-editorial-black text-background shadow-lg hover:shadow-elevated transition-all hover:-translate-y-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-editorial-black focus-visible:ring-offset-2"
            >
              <div className="relative z-10 flex flex-col items-center justify-center py-12 px-6 text-center">
                <div className="mb-4 rounded-full border border-primary/40 p-3 bg-white/5 group-hover:bg-primary group-hover:text-editorial-black transition-colors">
                  <span className="material-symbols-outlined text-3xl" aria-hidden="true">center_focus_strong</span>
                </div>
                <h2 className="text-xl font-bold tracking-[0.15em] mb-2 uppercase text-primary font-sans">
                  Take a Photo
                </h2>
                <p className="font-serif italic text-lg text-gray-300 font-light">
                  We'll identify your plant automatically
                </p>
              </div>
            </button>

            {/* I Know The Name — light card */}
            <button
              onClick={handleManualMethod}
              className="group w-full relative overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm hover:shadow-md transition-all hover:-translate-y-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-editorial-black focus-visible:ring-offset-2"
            >
              <div className="relative z-10 flex flex-col items-center justify-center py-12 px-6 text-center">
                <div className="mb-4 rounded-full bg-primary/10 p-3 text-editorial-black group-hover:bg-primary transition-colors">
                  <span className="material-symbols-outlined text-3xl" aria-hidden="true">edit_note</span>
                </div>
                <h2 className="text-xl font-bold tracking-[0.15em] mb-2 uppercase text-editorial-black font-sans">
                  I Know The Name
                </h2>
                <p className="font-serif italic text-lg text-gray-500 font-light">
                  Enter the plant name manually
                </p>
              </div>
            </button>
          </div>

          <footer className="px-6 py-6 mt-auto">
            <div className="editorial-divider mb-6" />
            <p className="text-center text-sm font-serif italic text-gray-500">
              Chloropal identifies over 30,000 species
            </p>
          </footer>
        </div>
      )}

      {/* Non-method steps get padding wrapper */}
      {step !== 'method' && <div className="px-6 py-4 space-y-6" />}

      {/* Step: Photo capture */}
      {step === 'capture' && (
        <div className="space-y-6 px-6">
          <p className="font-serif italic text-gray-600">
            Take a clear photo of your plant for the best results.
          </p>
          <PhotoCapture
            onCapture={handlePhotoCapture}
            onError={(msg) => setError(msg)}
          />
        </div>
      )}

      {/* Step: Manual name entry */}
      {step === 'manual' && (
        <div className="space-y-6 px-6">
          <p className="font-serif italic text-gray-600">
            Enter the common or scientific name of your plant.
          </p>
          <NameInput
            value={manualName}
            onChange={setManualName}
            onSubmit={handleValidateName}
            error={error}
          />
          <button
            onClick={handleValidateName}
            disabled={manualName.trim().length < 2}
            className="btn-editorial group disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span>Look Up Plant</span>
            <span className="material-symbols-outlined group-hover:translate-x-1 transition-transform" style={{ fontSize: 20 }} aria-hidden="true">search</span>
          </button>
        </div>
      )}

      {/* Step: Validating name */}
      {step === 'validating' && (
        <div className="flex flex-col items-center justify-center py-16 px-6">
          <span className="material-symbols-outlined text-primary animate-pulse mb-6" style={{ fontSize: 48 }} aria-hidden="true">search</span>
          <p className="text-lg font-display italic text-editorial-black" role="status" aria-live="polite">
            Looking up &quot;{manualName}&quot;...
          </p>
          <p className="mt-2 text-sm font-serif text-gray-500">
            This may take a few seconds
          </p>
        </div>
      )}

      {/* Step: Suggestions */}
      {step === 'suggestions' && (
        <div className="space-y-6 px-6">
          <NameSuggestions
            suggestions={suggestions}
            onSelect={handleSelectSuggestion}
            onAddCustom={handleAddCustom}
          />
        </div>
      )}

      {/* Step: Custom plant details */}
      {step === 'custom-details' && identification && (
        <div className="space-y-6 px-6">
          <div className="rounded-lg bg-warning/10 border border-warning/20 p-4">
            <p className="text-sm font-sans text-warning">
              Custom plants won&apos;t have AI-generated care information, but you can still track watering and add notes.
            </p>
          </div>
          <PlantDetailsForm
            commonName={identification.commonName}
            scientificName=""
            photoUrl=""
            onSubmit={handleSavePlant}
            onCancel={handleBack}
          />
        </div>
      )}

      {/* Step: Rate limited */}
      {step === 'rate-limited' && (
        <RateLimitMessage
          resetIn={rateLimitResetMs}
          onEnterManually={() => {
            setRateLimitResetMs(0);
            setStep('manual');
          }}
        />
      )}

      {/* Step: Identifying */}
      {step === 'identifying' && (
        <div className="flex flex-col items-center justify-center py-16 px-6">
          <span className="material-symbols-outlined text-primary animate-pulse mb-6" style={{ fontSize: 48 }} aria-hidden="true">eco</span>
          <p className="text-lg font-display italic text-editorial-black" role="status" aria-live="polite">
            Identifying your plant...
          </p>
          <p className="mt-3 text-sm font-serif italic text-gray-500 transition-opacity duration-300">
            {IDENTIFYING_MESSAGES[loadingMessageIndex % IDENTIFYING_MESSAGES.length]}
          </p>
        </div>
      )}

      {/* Step: Recovery */}
      {step === 'recovery' && (
        <MismatchRecovery
          recoveryStep={recoveryView}
          originalPhotoUrl={photoPreviewUrl}
          wrongIdentificationName={wrongIdentificationName}
          isProcessing={isRecoveryProcessing}
          error={error}
          onSubmitContext={handleRecoveryDescribe}
          onRetakeCapture={handleRecoveryRetake}
          onSetView={setRecoveryView}
          onManualEntry={handleManualMethod}
        />
      )}

      {/* Step: Result */}
      {step === 'result' && identification && (
        <IdentificationResult
          userPhotoUrl={photoPreviewUrl}
          referencePhotoUrl={referencePhotoUrl}
          commonName={identification.commonName}
          scientificName={identification.scientificName}
          confidence={identification.confidence}
          alternatives={identification.alternatives}
          onConfirm={handleConfirmIdentification}
          onSelectAlternative={handleSelectAlternative}
          onManualEntry={handleManualMethod}
          onNotMyPlant={handleNotMyPlant}
        />
      )}

      {/* Step: Details form */}
      {step === 'details' && identification && (
        <div className="space-y-6 px-6">
          <PlantDetailsForm
            commonName={identification.commonName}
            scientificName={identification.scientificName}
            photoUrl={photoPreviewUrl || referencePhotoUrl}
            onSubmit={handleSavePlant}
            onCancel={handleBack}
          />
        </div>
      )}

      {/* Step: Saving */}
      {step === 'saving' && (
        <div className="flex flex-col items-center justify-center py-16 px-6">
          <span className="material-symbols-outlined text-primary animate-pulse mb-6" style={{ fontSize: 48 }} aria-hidden="true">
            {isCustomPlant ? 'potted_plant' : 'auto_awesome'}
          </span>
          <p className="text-lg font-display italic text-editorial-black" role="status" aria-live="polite">
            {isCustomPlant ? 'Saving your plant...' : 'Generating care guide...'}
          </p>
          <p className="mt-3 text-sm font-serif italic text-gray-500 transition-opacity duration-300">
            {isCustomPlant
              ? 'Adding to your collection...'
              : SAVING_MESSAGES[loadingMessageIndex % SAVING_MESSAGES.length]}
          </p>
        </div>
      )}

      {/* Success Modal */}
      <SaveSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        plantName={identification?.commonName || 'Plant'}
        plantId={savedPlantId}
        onViewPlant={handleViewPlant}
        onAddAnother={handleAddAnother}
        onDone={handleDone}
      />
    </main>
  );
}
